import { useState } from "react";
import { adminApi, imgOf, originalUrl } from "../lib";
import { Panel, In, Uploader, Thumbs, useAdmin, save, Check, SampleBadge } from "./parts";

const blank = { kind: "painting", title: "", year_text: "", sort_year: "", medium: "", dimensions: "", description: "", credit: "",
  rights: "", print_available: true, print_options: [], paper_options: [], frame_options: [], media_ids: [], image_captions: [], related_story_ids: [], status: "published" };

function ProcessPicker({ a, setA }) {
  const p = a.process || {};
  const opts = [["", "—"], ...a.media_ids.map((m, i) => [m, `Photo ${i + 1}${a.image_captions?.[i] ? ` — ${a.image_captions[i]}` : ""}`])];
  const set = (k) => (e) => setA({ ...a, process: { ...p, [k]: e.target.value } });
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="art-process">
      {[["before", "Before & after: in-progress photo"], ["after", "Before & after: finished photo"]].map(([k, l]) => (
        <label key={k} className="block"><span className="lou-label">{l}</span>
          <select className="lou-input text-sm !py-2" value={p[k] || ""} onChange={set(k)} data-testid={`art-process-${k}`}>
            {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></label>))}
    </div>
  );
}

function ArtForm({ a, setA, onSave, onCancel, stories }) {
  const set = (k) => (v) => setA({ ...a, [k]: v });
  return (
    <div className="border border-lou-clay/40 bg-lou-paper p-4 mb-6 space-y-4" data-testid="admin-art-form">
      <div className="grid md:grid-cols-3 gap-4">
        <label className="block"><span className="lou-label">Collection</span>
          <select className="lou-input text-sm !py-2" value={a.kind} onChange={(e) => set("kind")(e.target.value)} data-testid="art-kind">
            <option value="painting">Painting</option><option value="sculpture">Sculpture</option></select></label>
        <In label="Title (leave blank if untitled)" value={a.title} onChange={set("title")} testid="art-title" />
        <In label="Year or approximate date" placeholder="e.g. c. 1990, or blank" value={a.year_text} onChange={set("year_text")} testid="art-year" />
        <In label="Year for ordering (optional)" type="number" value={a.sort_year ?? ""} onChange={set("sort_year")} />
        <In label="Medium" value={a.medium} onChange={set("medium")} testid="art-medium" />
        <In label="Dimensions" value={a.dimensions} onChange={set("dimensions")} testid="art-dimensions" />
      </div>
      <In label="Description or history" textarea value={a.description} onChange={set("description")} testid="art-description" />
      {a.media_ids.length > 1 && <ProcessPicker a={a} setA={setA} />}
      <div className="grid md:grid-cols-2 gap-4">
        <In label="Display credit" value={a.credit} onChange={set("credit")} testid="art-credit" />
        <In label="Rights information" value={a.rights} onChange={set("rights")} testid="art-rights" />
      </div>
      <div>
        <span className="lou-label">Photographs & detail views (first is the main image)</span>
        <div className="flex flex-wrap items-end gap-4">
          <Thumbs ids={a.media_ids} sample={a.sample_urls?.[0]} onRemove={(id) => set("media_ids")(a.media_ids.filter((x) => x !== id))} />
          <Uploader multiple label="Add photographs" testid="art-upload" onUploaded={(ms) => set("media_ids")([...a.media_ids, ...ms.map((m) => m.id)])} />
        </div>
        {a.media_ids.length > 0 && <p className="text-xs text-lou-soft mt-2">Originals are stored privately; {a.media_ids.map((m, i) => <a key={m} href={originalUrl(m)} className="underline mr-2">original {i + 1}</a>)}</p>}
      </div>
      <div className="flex flex-wrap gap-6 items-center">
        <Check label="Print inquiries open (shows “Request a Print”)" checked={a.print_available} onChange={set("print_available")} testid="art-print-available" />
        <Check label="Published" checked={a.status === "published"} onChange={(on) => set("status")(on ? "published" : "draft")} testid="art-published" />
      </div>
      {a.print_available && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="art-print-config">
          {[["print_options", "Print sizes to suggest", "leave blank until Greg confirms"], ["paper_options", "Papers / finishes to suggest", "added to Smooth matte, Textured fine art paper"],
            ["frame_options", "Frame choices (future ordering)", "not shown to visitors yet"]].map(([k, l, ph]) => (
            <In key={k} label={`${l} (comma separated)`} placeholder={ph} testid={`art-${k}`}
              value={a[`${k}_raw`] ?? (a[k] || []).join(", ")}
              onChange={(v) => setA({ ...a, [`${k}_raw`]: v, [k]: v.split(",").map((x) => x.trim()).filter(Boolean) })} />))}
        </div>
      )}
      <label className="block"><span className="lou-label">Related memories</span>
        <select multiple className="lou-input text-sm h-24" value={a.related_story_ids || []} onChange={(e) => set("related_story_ids")(Array.from(e.target.selectedOptions).map((o) => o.value))}>
          {(stories || []).filter((s) => s.status === "approved").map((s) => <option key={s.id} value={s.id}>{s.title || s.body.slice(0, 50)} — {s.display_name}</option>)}
        </select></label>
      <div className="flex gap-3"><button className="lou-btn !py-2 text-sm" onClick={onSave} data-testid="art-save">Save artwork</button>
        <button className="lou-btn-ghost text-sm" onClick={onCancel}>Cancel</button></div>
    </div>
  );
}

export default function ArtTab() {
  const [items, load] = useAdmin("/c/artworks");
  const [stories] = useAdmin("/stories");
  const [settings, , setSettings] = useAdmin("/settings");
  const [edit, setEdit] = useState(null);
  const submit = async () => {
    const body = { ...edit, sort_year: edit.sort_year ? Number(edit.sort_year) : null };
    ["print_options_raw", "paper_options_raw", "frame_options_raw"].forEach((k) => delete body[k]);
    if (body.media_ids.length) { body.sample_urls = []; body.is_sample = false; }
    const ok = await save(() => (edit.id ? adminApi.put(`/c/artworks/${edit.id}`, body) : adminApi.post("/c/artworks", body)));
    if (ok) { setEdit(null); load(); }
  };
  const del = async (id) => { if (window.confirm("Delete this artwork record? Uploaded originals remain in the archive.")) { await save(() => adminApi.delete(`/c/artworks/${id}`), "Deleted"); load(); } };
  return (
    <>
    {settings && (
      <Panel title="Artist statement — in Louise's words" testid="admin-artist-statement"
        actions={<button className="lou-btn !py-2 text-sm" data-testid="admin-statement-save"
          onClick={() => save(() => adminApi.put("/settings", { artist_statement: settings.artist_statement, art_quote: settings.art_quote, sculpture_intro: settings.sculpture_intro }))}>Save statement</button>}>
        <div className="mb-5"><In label="Short quote shown above the statement" textarea rows={3}
          value={settings.art_quote} onChange={(v) => setSettings({ ...settings, art_quote: v })} testid="admin-art-quote" /></div>
        <In label="Shown at the top of Her Art. Leave a blank line between paragraphs." textarea rows={10}
          value={settings.artist_statement} onChange={(v) => setSettings({ ...settings, artist_statement: v })} testid="admin-statement-text" />
        <div className="mt-4"><In label="Sculpture introduction (shown above the Sculptures gallery)" textarea rows={6}
          value={settings.sculpture_intro} onChange={(v) => setSettings({ ...settings, sculpture_intro: v })} testid="admin-sculpture-intro" /></div>
      </Panel>
    )}
    <Panel title="Paintings & sculptures" testid="admin-art"
      actions={<button className="lou-btn-ghost text-sm" onClick={() => setEdit({ ...blank })} data-testid="admin-art-add">Add artwork</button>}>
      {edit && <ArtForm a={edit} setA={setEdit} onSave={submit} onCancel={() => setEdit(null)} stories={stories} />}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(items || []).map((a) => (
          <div key={a.id} className="border border-lou-line p-3 flex gap-3" data-testid={`admin-art-${a.id}`}>
            <img src={imgOf(a, true)} alt="" className="w-20 h-20 object-contain bg-lou-ivory shrink-0" />
            <div className="text-sm min-w-0">
              <p className="font-medium truncate">{a.title || "Untitled"}<SampleBadge show={a.is_sample} /></p>
              <p className="text-lou-soft capitalize">{a.kind} · {a.year_text || "undated"} · {a.status}</p>
              {a.print_available && <p className="text-xs text-lou-clay">prints on request</p>}
              <div className="flex gap-3 mt-1"><button className="underline" onClick={() => setEdit({ ...blank, ...a })} data-testid={`admin-art-edit-${a.id}`}>Edit</button>
                <button className="underline text-red-700" onClick={() => del(a.id)}>Delete</button></div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
    </>
  );
}
